import { useSessionKey } from "@auth/sessionStore";
import { CallSuggestions } from "@notifications/CallSuggestionsPanel";
import { patientsClient } from "@patients/patientsClient";
import { signalmentOf } from "@patients/signalment";
import { CallButton } from "@phone/CallButton";
import { callWhen } from "@phone/CallLog";
import { callParty, useCallTask } from "@phone/callParty";
import { useCallSuggestions } from "@phone/callSuggestionsClient";
import {
  callOutcome,
  formatDuration,
  isSummaryPending,
} from "@phone/phoneCallOutcome";
import type { PhoneCall } from "@phone/phoneCallsClient";
import { formatPhoneNumber } from "@phone/phoneSetupClient";
import { RecordingPlayer } from "@phone/RecordingPlayer";
import {
  ClockIcon,
  PanelError,
  PatientSprite,
  StateCheckbox,
} from "@scribenote/brian-design-system";
import { useQuery } from "@tanstack/react-query";
import { useCallTodos } from "@tasks/tasksQueries";
import { TodoRow, todoCheckState } from "@todos/TodoRow";
import { displayNameOf, useClinicUsers } from "@users/userQueries";
import { useNow } from "@utils/use-now";
import type { ReactNode } from "react";

const LABEL =
  "font-mono text-fine-print uppercase tracking-[0.14em] text-annotation";

/** `messageState` only picks this copy, never gates `message`: older calls carry a message under `none`. */
const noMessageLine = (call: PhoneCall): string | null => {
  if (call.message || call.origin === "staff_outbound") return null;
  if (call.messageState === "no_message") return "No message left.";
  if (call.messageState === "none" && call.disposition === "caller_abandoned")
    return "Hung up before leaving a message.";
  return null;
};

const dueFormat = new Intl.DateTimeFormat("en", {
  weekday: "short",
  month: "short",
  day: "numeric",
});

const useOnlyPatient = (clientId: string | null) => {
  const { data } = useQuery({
    queryKey: ["patients", "for-client", clientId],
    queryFn: () => patientsClient.listForClient(clientId!),
    enabled: clientId !== null,
    staleTime: 10 * 60_000,
  });
  const only = data?.length === 1 ? data[0] : null;
  return only
    ? {
        id: only.id,
        signalment: signalmentOf(only.signalment),
        sprite: only.sprite,
      }
    : null;
};

/** Clinical text throughout: keep it out of Session Replay. */
export const CallDetail = ({
  call,
  showSuggestions = true,
}: {
  call: PhoneCall;
  showSuggestions?: boolean;
}) => {
  const party = callParty(call);
  const outcome = callOutcome(call);
  const now = useNow();
  const summaryPending = isSummaryPending(call, now);
  // Outbound `started_at` is the bridge (brian modules/phone/outbound_transitions.py): never bridged, no duration.
  const duration = formatDuration(call.startedAt, call.endedAt);
  const viaLine = party.outbound
    ? (call.staffLineLabel ??
      (call.staffLineE164 ? formatPhoneNumber(call.staffLineE164) : null))
    : null;
  const patient = useOnlyPatient(call.clientUuid);
  const breed = patient?.signalment.breed ?? null;
  const meta = [
    call.clientPatients?.length ? call.clientPatients.join(", ") : null,
    breed,
    party.number,
  ].filter(Boolean);
  const noMessage = noMessageLine(call);

  return (
    <article className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-md border border-rule bg-card px-[30px] py-6 shadow-sheet">
      <header className="flex items-start justify-between gap-6 border-b-[1.5px] border-ink pb-3.5">
        <div className="flex min-w-0 items-center gap-4">
          {patient && <PatientSprite size={44} sprite={patient.sprite} />}
          <div className="min-w-0">
            <h2
              className="truncate font-display text-[1.625rem] font-medium leading-tight tracking-[-0.01em] text-ink"
              data-dd-privacy="hidden"
            >
              {party.name}
            </h2>
            {meta.length > 0 && (
              <p
                className="mt-0.5 truncate text-[0.9375rem] text-annotation"
                data-dd-privacy="hidden"
              >
                {meta.join(" · ")}
              </p>
            )}
          </div>
        </div>
        {party.callbackE164 && (
          <CallButton
            destination={{ kind: "number", e164: party.callbackE164 }}
            label="Call back"
            size="md"
            buttonClassName="rounded-md border-[1.5px] border-rule-strong px-[22px] font-semibold"
            testId="phone-call-callback"
            compact
          />
        )}
      </header>

      <p className="flex flex-wrap items-center gap-x-6 gap-y-1 border-b border-dotted border-rule-strong py-3 text-mono-time text-sm text-ink-body">
        <time dateTime={call.createdAt} className="inline-flex items-center">
          <ClockIcon className="mr-[0.35em]" />
          {callWhen(call.createdAt)}
        </time>
        <span>
          {[party.outbound ? "Outgoing" : "Incoming", duration]
            .filter(Boolean)
            .join(" · ")}
        </span>
        {viaLine && <span data-dd-privacy="hidden">via {viaLine}</span>}
        <span>{outcome.helper ?? outcome.label}</span>
      </p>

      {call.summary && (
        <p
          className="mt-5 whitespace-pre-line break-words text-[1.1875rem] leading-snug text-ink"
          data-dd-privacy="hidden"
          data-testid="phone-call-summary"
        >
          {call.summary}
        </p>
      )}
      {summaryPending && (
        <p className="mt-5 text-sm text-annotation" role="status">
          Summarizing the call.
        </p>
      )}

      {call.message && (
        <Section label="Message left">
          <p
            className="whitespace-pre-line break-words font-hand text-2xl leading-snug text-ink"
            data-dd-privacy="hidden"
            data-testid="phone-call-message"
          >
            {call.message}
          </p>
        </Section>
      )}
      {noMessage && <p className="mt-4 text-sm text-annotation">{noMessage}</p>}

      {call.messageTaskUuid && <CallTodo taskId={call.messageTaskUuid} />}
      {!party.outbound && showSuggestions && <CallFollowups call={call} />}

      {call.recordingArtifactUuid && (
        <Section label="Recording">
          <RecordingPlayer artifactId={call.recordingArtifactUuid} />
        </Section>
      )}
    </article>
  );
};

const Section = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <section className="mt-6 flex flex-col gap-2.5">
    <h3 className={LABEL}>{label}</h3>
    {children}
  </section>
);

const CallTodo = ({ taskId }: { taskId: string }) => {
  const sessionKey = useSessionKey();
  const { data: task } = useCallTask(taskId);
  const { data: users } = useClinicUsers(sessionKey);
  if (!task) return null;
  const owner = task.assigneeUserId
    ? displayNameOf(users, task.assigneeUserId, "")
    : "";

  return (
    <Section label="To-dos">
      <div className="flex items-center gap-3.5 border-b border-rule pb-3">
        <StateCheckbox state={todoCheckState(task.status)} />
        <span
          className="min-w-0 flex-1 text-[0.9375rem] font-semibold text-ink"
          data-dd-privacy="hidden"
        >
          {task.title}
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          {owner && (
            <span className="rounded-tag border border-rule-strong px-2 py-0.5 font-mono text-fine-print uppercase leading-none tracking-[0.08em] text-annotation">
              {owner}
            </span>
          )}
          {task.dueAt && (
            <time
              dateTime={task.dueAt}
              className="text-mono-time text-xs text-annotation"
            >
              {dueFormat.format(new Date(task.dueAt)).replace(",", "")}
            </time>
          )}
        </span>
      </div>
    </Section>
  );
};

/**
 * Live proposals stay in the suggestions panel until the hang-up countdown
 * files them; filing deletes the call's suggestion rows, so after that (or
 * when an ended call had none) the call's own to-dos are the record.
 */
const CallFollowups = ({ call }: { call: PhoneCall }) => {
  const suggestions = useCallSuggestions(call.id);
  const data = suggestions.data;
  const settled =
    data !== undefined &&
    (data.autofile.filed_at !== null ||
      (call.status === "ended" && data.items.length === 0));
  return settled ? (
    <CallTodos call={call} />
  ) : (
    <CallSuggestions callUuid={call.id} query={suggestions} />
  );
};

const CallTodos = ({ call }: { call: PhoneCall }) => {
  const sessionKey = useSessionKey();
  const todos = useCallTodos(sessionKey, call.id);
  // The message task already has its own row above.
  const tasks = (todos.data ?? []).filter(
    (task) => task.id !== call.messageTaskUuid,
  );
  if (todos.isError)
    return (
      <Section label="To-dos from this call">
        <PanelError label="to-dos" onRetry={() => void todos.refetch()} />
      </Section>
    );
  if (tasks.length === 0) return null;
  return (
    <Section label="To-dos from this call">
      <ul className="flex flex-col" data-dd-privacy="hidden">
        {tasks.map((task) => (
          <TodoRow key={task.id} task={task} className="py-2.5" />
        ))}
      </ul>
    </Section>
  );
};
